import {
  BodyTypeSelect,
  DriveTypeSelect,
  DrawerCloseButton,
  FuelTypeSelect,
  TransmissionSelect,
} from '@components';
import { L } from '@lib';
import { UpdateVehicle, Vehicle } from '@services';
import { useStore } from '@stores';
import { Rules } from '@utils';
import { Button, Col, DatePicker, Drawer, Form, Input, InputNumber, Row, Space } from 'antd';
import dayjs from 'dayjs';
import { observer } from 'mobx-react';
import React, { useCallback, useEffect } from 'react';

interface IProps {
  open: boolean;
  vehicle: Vehicle;
  onClose: () => void;
  onSuccess?: (vehicle: Vehicle) => void;
}

export const VehicleEditDrawer: React.FC<IProps> = observer(
  ({ open, vehicle, onClose, onSuccess }) => {
    const { vehicleStore } = useStore();
    const [form] = Form.useForm();

    // Vehicles 40+ years old are MOT-exempt, so we hide the expiry date picker.
    const exemptFromMot = vehicle.dateFirstRegistered
      ? dayjs().diff(vehicle.dateFirstRegistered, 'years') >= 40
      : false;

    useEffect(() => {
      if (open && vehicle) {
        form.setFieldsValue({
          ...vehicle,
          dateFirstRegistered: vehicle.dateFirstRegistered
            ? dayjs(vehicle.dateFirstRegistered)
            : undefined,
          startDateOfCurrentKeeper: vehicle.startDateOfCurrentKeeper
            ? dayjs(vehicle.startDateOfCurrentKeeper)
            : undefined,
          motExpiryDate: vehicle.motExpiryDate ? dayjs(vehicle.motExpiryDate) : undefined,
          taxDueDate: vehicle.taxDueDate ? dayjs(vehicle.taxDueDate) : undefined,
        });
      }
    }, [open, vehicle, form]);

    const handleFinish = useCallback(
      async (values: UpdateVehicle) => {
        await vehicleStore.update({ ...values, id: vehicle.id }).then((result) => {
          if (!result) return;
          onClose();
          onSuccess?.(result);
        });
      },
      [vehicleStore, vehicle.id, onClose, onSuccess]
    );

    return (
      <Drawer
        title={L('Update vehicle')}
        open={open}
        onClose={onClose}
        styles={{ wrapper: { width: 960 } }}
        destroyOnHidden
        extra={<DrawerCloseButton onClick={onClose} />}
        closable={false}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Space>
              <Button onClick={onClose}>{L('Cancel')}</Button>
              <Button type="primary" loading={vehicleStore.loading} onClick={() => form.submit()}>
                {L('Save changes')}
              </Button>
            </Space>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={handleFinish}>
          <Form.Item name="id" hidden>
            <Input />
          </Form.Item>
          <Row gutter={8} justify="space-between">
            <Col span={8}>
              <Form.Item name="registrationNumber" label={L('Registration number')}>
                <Input disabled placeholder={L('Registration number')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="combinedMake" label={L('Make')} rules={Rules.required}>
                <Input placeholder={L('Make')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="combinedModel" label={L('Model')} rules={Rules.required}>
                <Input placeholder={L('Model')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8} justify="space-between">
            <Col span={8}>
              <Form.Item
                name="combinedVin"
                label={L('Vin')}
                rules={[
                  { required: true, message: 'VIN is required' },
                  {
                    pattern: /^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$/,
                    message: 'Invalid VIN format',
                  },
                ]}
              >
                <Input
                  placeholder="VIN"
                  onChange={(e) => {
                    const value = e.target.value.toUpperCase();
                    form.setFieldsValue({ combinedVin: value });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="dvlaYearOfManufacture" label={L('Year of manufacture')}>
                <Input type="number" min={0} placeholder={L('Year of manufacture')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="combinedEngineCapacity" label={L('Engine size')}>
                <Input type="number" min={0} placeholder={L('Engine size')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8} justify="space-between">
            <Col span={8}>
              <Form.Item name="combinedFuelType" label={L('Fuel type')} rules={Rules.required}>
                <FuelTypeSelect />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="combinedTransmission" label={L('Gear type')} rules={Rules.required}>
                <TransmissionSelect />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="vehicleCategoryDescription" label={L('Vehicle type')}>
                <BodyTypeSelect />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8} justify="space-between">
            <Col span={8}>
              <Form.Item name="driveType" label={L('Drive type')}>
                <DriveTypeSelect />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="colour" label={L('Colour')}>
                <Input placeholder={L('Colour')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              {exemptFromMot ? (
                <Form.Item label={L('MOT expiry date')}>
                  <Input disabled value={L('Exempt from MOT')} />
                </Form.Item>
              ) : (
                <Form.Item name="motExpiryDate" label={L('MOT expiry date')}>
                  <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                </Form.Item>
              )}
            </Col>
          </Row>
          <Row gutter={8} justify="space-between">
            <Col span={8}>
              <Form.Item name="dateFirstRegistered" label={L('First registration')}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="startDateOfCurrentKeeper" label={L('Current keeper')}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="taxDueDate" label={L('Tax due')}>
                <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8} justify="space-between">
            <Col span={8}>
              <Form.Item name="euroStatus" label={L('Euro status')}>
                <Input placeholder={L('Euro status')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="modelSeries" label={L('Model series')}>
                <Input placeholder={L('Model series')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="wheelplan" label={L('Wheelbase type')}>
                <Input placeholder={L('Wheelbase type')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8} justify="space-between">
            <Col span={8}>
              <Form.Item name="engineNumber" label={L('Engine number')}>
                <Input placeholder={L('Engine number')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="grossWeightKg" label={L('Gross weight')}>
                <Input type="number" min={0} placeholder={L('Gross weight')} />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="taxStatus" label={L('Road tax')}>
                <Input placeholder={L('Road tax')} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={8} justify="space-between">
            <Col span={8}>
              {/* Invoicing only ever raises a vehicle's mileage, so this is where a mistyped reading is put right. */}
              <Form.Item
                name="mileage"
                label={L('Mileage')}
                tooltip={L('Jobs only ever raise this figure. Change it here to correct a wrong reading.')}
              >
                <InputNumber min={0} max={1000000} controls={false} style={{ width: '100%' }} placeholder={L('Mileage')} />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Drawer>
    );
  }
);
